import { readOpportunityTriggerConfig } from "encharge-domain/lib/helpers/salesforce/salesforceTriggers";
import { incompleteFilterProblem } from "./opportunityFilters.model";
import {
  SalesforceOpportunityEstimate,
  SalesforceOpportunityEstimateRequest,
  SalesforceOpportunityStatus,
  SalesforceTriggerRequestError,
} from "store/persistence/persistSalesforceTriggers";

/**
 * The two Salesforce v2 flow triggers ("Salesforce Opportunity" and
 * "Added to Salesforce Campaign"), worked out from a step's stored settings.
 * Pure functions with no React in them, so the words and the rules can be
 * tested on their own. Copy rules: plain words, no em dashes, "within about 2
 * minutes" for the Opportunity trigger and "within about 10 minutes" for the
 * campaign one, and never "real time". The wording is from the in-app copy file
 * (flow builder triggers); each key is named in a comment where it is used.
 *
 * The Opportunity trigger starts on a stage, on the Opportunity's name, or on
 * a field the marketer picks, with optional Filters (rules on the Opportunity)
 * and the three "who enters" choices.
 *
 * The settings are the ones the server stores and reads back
 * (packages/domain/helpers/salesforce/salesforceTriggers.ts, readOpportunityTriggerConfig
 * and readCampaignConfig): a picker's choice is the Salesforce id and the label
 * together, so a renamed stage keeps working and the card can show its name.
 * This file keeps its own copy of those shapes, as the rest of the frontend does
 * for the API's.
 */

export const SALESFORCE_SERVICE_ID = "salesforce";
/** The Opportunity trigger's operation. Its name says stage because it began as the stage trigger. */
export const OPPORTUNITY_STAGE_OPERATION = "/salesforce-opportunity";
export const OPPORTUNITY_OPERATION = OPPORTUNITY_STAGE_OPERATION;
export const CAMPAIGN_MEMBER_OPERATION = "/salesforce-campaign-member";

export const SALESFORCE_V2_TRIGGER_OPERATIONS: readonly string[] = [
  OPPORTUNITY_STAGE_OPERATION,
  CAMPAIGN_MEMBER_OPERATION,
];

/** Whether a step or a tool is one of the two new triggers. */
export const isSalesforceV2Trigger = (item: {
  serviceId?: string;
  operationKey?: string;
}): boolean =>
  item.serviceId === SALESFORCE_SERVICE_ID &&
  typeof item.operationKey === "string" &&
  SALESFORCE_V2_TRIGGER_OPERATIONS.includes(item.operationKey);

export const isOpportunityStageTrigger = (item: {
  serviceId?: string;
  operationKey?: string;
}): boolean =>
  item.serviceId === SALESFORCE_SERVICE_ID &&
  item.operationKey === OPPORTUNITY_STAGE_OPERATION;
export const isOpportunityTrigger = isOpportunityStageTrigger;

export const isCampaignTrigger = (item: {
  serviceId?: string;
  operationKey?: string;
}): boolean =>
  item.serviceId === SALESFORCE_SERVICE_ID &&
  item.operationKey === CAMPAIGN_MEMBER_OPERATION;

// ---------------------------------------------------------------------------
// Settings

/** A picker's choice: the Salesforce id, and the name it had when it was chosen. */
export type Choice = { id: string; label: string };
export type StatusChoice = Choice & { campaignId: string };

export type WhoEnters = "contactRoles" | "accountPeople" | "primaryContact";
export type OpportunityOutcome = "any" | "won" | "lost";

export type OpportunityStartsOn = "stage" | "name" | "field";

/** The field a Field start waits for: the Salesforce API name and the label the picker showed */
export type OpportunityFieldChoice = { name: string; label: string };

/** The Filters as the step stores them: `{ rules }`, the rule groups of the sync criteria */
export type OpportunityFilters = { rules: unknown };

export type OpportunityTriggerSettings = {
  /** Left out: a Stage start (a step saved before the Name and Field starts reads so). readOpportunitySettings always sets it. */
  startsOn?: OpportunityStartsOn;
  /** Stage start only. Empty means any stage */
  stages: Choice[];
  /** Stage start only */
  only: OpportunityOutcome;
  /** Field start only */
  field?: OpportunityFieldChoice | undefined;
  /** Optional, for any start */
  filters?: OpportunityFilters | undefined;
  /** Not chosen yet: nobody is picked for the marketer */
  whoEnters: WhoEnters | undefined;
};

export type CampaignWhen = "added" | "statusBecomes";

export type CampaignSettings = {
  campaigns: Choice[];
  when: CampaignWhen;
  /** Only read for "statusBecomes" */
  statuses: StatusChoice[];
};

export const WHO_ENTERS_VALUES: readonly WhoEnters[] = [
  "contactRoles",
  "accountPeople",
  "primaryContact",
];

export const isWhoEnters = (value: unknown): value is WhoEnters =>
  (WHO_ENTERS_VALUES as readonly unknown[]).includes(value);

/**
 * The three ways to choose who enters, in the words the marketer reads.
 * flow.opportunity.who.people / .account / .main and their .help lines, and
 * flow.opportunity.who.account.example for the company example.
 */
export const WHO_ENTERS_OPTIONS: ReadonlyArray<{
  value: WhoEnters;
  label: string;
  help: string;
  example?: string;
}> = [
  {
    value: "contactRoles",
    label: "People on the Opportunity",
    help:
      "The Contacts listed on the Opportunity as Contact Roles. An Opportunity with nobody listed starts nothing.",
  },
  {
    value: "accountPeople",
    label: "Everyone at the Opportunity's Account",
    help:
      "People whose main Account is the Opportunity's Account. A person who is only related to the Account does not enter. An Opportunity with no Account starts nothing.",
    example:
      "For example, an Opportunity at Brightwell Logistics brings in everyone whose main Account is Brightwell Logistics. Add a Check Condition step after the trigger to keep only the people you want, such as those with a certain job title.",
  },
  {
    value: "primaryContact",
    label: "The Opportunity's main contact",
    help:
      "The primary Contact Role on the Opportunity. An Opportunity with no primary contact starts nothing.",
  },
];

/** What starts the flow: the settings' own start, or the Stage start when they have none. */
export const startsOnOf = (
  settings: Pick<OpportunityTriggerSettings, "startsOn">
): OpportunityStartsOn => settings.startsOn ?? "stage";

export const isStartsOn = (value: unknown): value is OpportunityStartsOn =>
  value === "stage" || value === "name" || value === "field";

/** What starts the flow, in the words the marketer reads (flow.opportunity.starts.*) */
export const OPPORTUNITY_STARTS_ON_OPTIONS: ReadonlyArray<{
  value: OpportunityStartsOn;
  label: string;
  help: string;
}> = [
  {
    value: "stage",
    label: "Stage",
    help:
      "The flow starts when an Opportunity moves to a stage you choose. An Opportunity created already in one of these stages counts too.",
  },
  {
    value: "name",
    label: "Opportunity Name",
    help: "The flow starts when an Opportunity's name changes.",
  },
  {
    value: "field",
    label: "Opportunity Field",
    help:
      "Pick one field. The flow starts when its value changes. A new Opportunity that has a value in it counts too.",
  },
];

export const OPPORTUNITY_OUTCOME_OPTIONS: ReadonlyArray<{
  value: OpportunityOutcome;
  label: string;
}> = [
  // flow.opportunity.outcome.none / .won / .lost
  { value: "any", label: "All Opportunities" },
  { value: "won", label: "Only won Opportunities" },
  { value: "lost", label: "Only lost Opportunities" },
];

export const CAMPAIGN_WHEN_OPTIONS: ReadonlyArray<{
  value: CampaignWhen;
  label: string;
}> = [
  // flow.campaign.when.added / .status
  { value: "added", label: "A person is added to the campaign" },
  { value: "statusBecomes", label: "A member's status becomes" },
];

/** A value as a list: a list, or a list written as JSON text (how a form can keep one). */
const asList = (raw: unknown): unknown[] => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim() !== "") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** The choices a step holds. Anything without a Salesforce id is not a choice. */
const readChoices = (raw: unknown): Choice[] => {
  const seen = new Set<string>();
  const choices: Choice[] = [];
  asList(raw).forEach((item) => {
    if (!isRecord(item) || typeof item.id !== "string" || item.id === "") {
      return;
    }
    if (seen.has(item.id)) return;
    seen.add(item.id);
    choices.push({
      id: item.id,
      label:
        typeof item.label === "string" && item.label.trim() !== ""
          ? item.label.trim()
          : item.id,
    });
  });
  return choices;
};

const readStatusChoices = (raw: unknown): StatusChoice[] => {
  const seen = new Set<string>();
  const choices: StatusChoice[] = [];
  asList(raw).forEach((item) => {
    if (
      !isRecord(item) ||
      typeof item.id !== "string" ||
      item.id === "" ||
      typeof item.campaignId !== "string" ||
      item.campaignId === ""
    ) {
      return;
    }
    const key = `${item.campaignId}-${item.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    choices.push({
      id: item.id,
      campaignId: item.campaignId,
      label:
        typeof item.label === "string" && item.label.trim() !== ""
          ? item.label.trim()
          : item.id,
    });
  });
  return choices;
};

/** The field a Field start waits for, from stored values. Anything without an API name is not a choice. */
const readFieldChoice = (raw: unknown): OpportunityFieldChoice | undefined => {
  const value =
    typeof raw === "string" && raw.trim() !== "" ? safeJson(raw) : raw;
  if (!isRecord(value) || typeof value.name !== "string" || value.name === "") {
    return undefined;
  }
  return {
    name: value.name,
    label:
      typeof value.label === "string" && value.label.trim() !== ""
        ? value.label.trim()
        : value.name,
  };
};

const safeJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch (e) {
    return undefined;
  }
};

/** The Filters a step holds, whole as stored; nothing when there are none. */
const readFilters = (raw: unknown): OpportunityFilters | undefined => {
  const value =
    typeof raw === "string" && raw.trim() !== "" ? safeJson(raw) : raw;
  return isRecord(value) && value.rules !== undefined && value.rules !== null
    ? { rules: value.rules }
    : undefined;
};

/** The Opportunity trigger's settings from a step's stored values (empty ones when it has none). A step saved before the Name and Field starts is a Stage start. */
export const readOpportunitySettings = (
  values: unknown
): OpportunityTriggerSettings => {
  const v = isRecord(values) ? values : {};
  return {
    startsOn: isStartsOn(v.startsOn) ? v.startsOn : "stage",
    stages: readChoices(v.stages),
    only: v.only === "won" || v.only === "lost" ? v.only : "any",
    field: readFieldChoice(v.field),
    filters: readFilters(v.filters),
    whoEnters: isWhoEnters(v.whoEnters) ? v.whoEnters : undefined,
  };
};

export const readCampaignSettings = (values: unknown): CampaignSettings => {
  const v = isRecord(values) ? values : {};
  return {
    campaigns: readChoices(v.campaigns),
    when: v.when === "statusBecomes" ? "statusBecomes" : "added",
    statuses: readStatusChoices(v.statuses),
  };
};

/**
 * What is saved for the Opportunity trigger. Call it only after
 * opportunityProblems found none. The settings of another start are not saved
 * (the server does not read them): stages and "limit to" are the Stage start's,
 * the field is the Field start's. Filters are saved when there are any.
 */
export const opportunityFormValues = (settings: OpportunityTriggerSettings) => {
  const startsOn = startsOnOf(settings);
  return {
    startsOn,
    stages: startsOn === "stage" ? settings.stages : [],
    only: startsOn === "stage" ? settings.only : ("any" as OpportunityOutcome),
    ...(startsOn === "field" && settings.field
      ? { field: settings.field }
      : {}),
    filters: settings.filters,
    whoEnters: settings.whoEnters,
  };
};

/**
 * What is saved for the Campaign trigger. Statuses left over from an earlier
 * choice of "added", or of a campaign that is no longer chosen, are not saved:
 * the server refuses a status whose campaign is not chosen.
 */
export const campaignFormValues = (settings: CampaignSettings) => ({
  campaigns: settings.campaigns,
  when: settings.when,
  statuses:
    settings.when === "statusBecomes"
      ? pruneStatuses(settings.statuses, settings.campaigns)
      : [],
});

/** The statuses whose campaign is still chosen. */
export const pruneStatuses = (
  statuses: StatusChoice[],
  campaigns: Choice[]
): StatusChoice[] => {
  const chosen = new Set(campaigns.map((campaign) => campaign.id));
  return statuses.filter((status) => chosen.has(status.campaignId));
};

export type OpportunityProblems = {
  field?: string;
  filters?: string;
  whoEnters?: string;
};

/**
 * What stops the Opportunity trigger from being saved, or undefined: the field a
 * Field start waits for, Filters the server would refuse (it reads them with the
 * rule engine, so this asks the same reader), and who enters.
 */
export const opportunityProblems = (
  settings: OpportunityTriggerSettings
): OpportunityProblems | undefined => {
  const problems: OpportunityProblems = {};
  if (startsOnOf(settings) === "field" && !settings.field) {
    // flow.opportunity.field.required
    problems.field = "Pick the field that starts the flow.";
  }
  if (settings.filters) {
    const missing = incompleteFilterProblem(settings.filters);
    if (missing) {
      problems.filters = missing;
    } else {
      const read = readOpportunityTriggerConfig({
        startsOn: "name",
        whoEnters: "contactRoles",
        filters: settings.filters,
      });
      if (!read.ok) problems.filters = read.problems[0];
    }
  }
  if (settings.whoEnters === undefined) {
    problems.whoEnters = "Choose who enters the flow.";
  }
  return problems.field || problems.filters || problems.whoEnters
    ? problems
    : undefined;
};
/** @deprecated the name before the Name and Field starts: the who-enters problem only */
export const opportunityStageProblem = (
  settings: OpportunityTriggerSettings
): string | undefined => opportunityProblems(settings)?.whoEnters;

/** What stops the Campaign trigger from being saved, or undefined. */
export const campaignProblem = (
  settings: CampaignSettings
): { campaigns?: string; statuses?: string } | undefined => {
  const problem: { campaigns?: string; statuses?: string } = {};
  if (settings.campaigns.length === 0) {
    problem.campaigns = "Pick at least one campaign.";
  } else if (
    settings.when === "statusBecomes" &&
    pruneStatuses(settings.statuses, settings.campaigns).length === 0
  ) {
    problem.statuses = "Pick a status.";
  }
  return problem.campaigns || problem.statuses ? problem : undefined;
};

/** The body the estimate route reads, or null while the settings cannot be counted yet. */
export const estimateRequest = (
  settings: OpportunityTriggerSettings
): SalesforceOpportunityEstimateRequest | null => {
  if (settings.whoEnters === undefined) return null;
  if (startsOnOf(settings) === "field" && !settings.field) return null;
  const values = opportunityFormValues(settings);
  return {
    startsOn: values.startsOn,
    stages: values.stages.map((stage) => ({
      id: stage.id,
      label: stage.label,
    })),
    only: values.only,
    ...("field" in values && values.field ? { field: values.field } : {}),
    ...(settings.filters ? { filters: settings.filters } : {}),
    whoEnters: settings.whoEnters,
  };
};

// ---------------------------------------------------------------------------
// Card text (flow.opportunity.preview, flow.campaign.preview)

const MAX_NAMES = 3;

/** "A", "A or B", "A, B or C", and past three names "A, B or 3 more". */
const orList = (items: string[]): string => {
  if (items.length <= 1) return items.join("");
  if (items.length <= MAX_NAMES) {
    return `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`;
  }
  const shown = items.slice(0, MAX_NAMES - 1);
  return `${shown.join(", ")} or ${items.length - shown.length} more`;
};

const quoted = (labels: string[]) => labels.map((label) => `'${label}'`);

const OPPORTUNITY_WHO_TEXT: Record<WhoEnters, string> = {
  // flow.opportunity.preview.who.people / .account / .main
  contactRoles: "the people on the Opportunity",
  accountPeople: "everyone at its Account",
  primaryContact: "its main contact",
};

/**
 * The sentence on the Opportunity trigger's card, for example "When a Salesforce
 * Opportunity moves to Closed Won, for everyone at its Account", "When a
 * Salesforce Opportunity's Amount changes, for the people on the Opportunity" or
 * "When a Salesforce Opportunity's name changes (with Filters), for its main
 * contact". The same sentences as packages/domain/helpers/salesforce/
 * salesforceTriggers.ts describeOpportunityTrigger (a spec compares them).
 * Undefined when the settings are not complete (the card then shows what the
 * server wrote).
 */
export const describeOpportunity = (
  settings: OpportunityTriggerSettings
): string | undefined => {
  if (settings.whoEnters === undefined) return undefined;
  const startsOn = startsOnOf(settings);
  if (startsOn === "field" && !settings.field) return undefined;
  const extras = [
    ...(startsOn === "stage" && settings.only !== "any"
      ? [`${settings.only} Opportunities only`]
      : []),
    ...(settings.filters ? ["with Filters"] : []),
  ];
  const tail = extras.length > 0 ? ` (${extras.join(", ")})` : "";
  let what: string;
  if (startsOn === "name") {
    what = "'s name changes";
  } else if (startsOn === "field") {
    what = `'s ${settings.field?.label || settings.field?.name} changes`;
  } else {
    what =
      settings.stages.length === 0
        ? " changes stage"
        : ` moves to ${orList(settings.stages.map((stage) => stage.label))}`;
  }
  return `When a Salesforce Opportunity${what}${tail}, for ${
    OPPORTUNITY_WHO_TEXT[settings.whoEnters]
  }`;
};

export const describeCampaign = (
  settings: CampaignSettings
): string | undefined => {
  if (settings.campaigns.length === 0) return undefined;
  const one = settings.campaigns.length === 1;
  if (settings.when === "added") {
    return one
      ? `When someone is added to the Salesforce campaign '${settings.campaigns[0].label}'`
      : `When someone is added to any of ${settings.campaigns.length} Salesforce campaigns`;
  }
  const statuses = pruneStatuses(settings.statuses, settings.campaigns);
  if (statuses.length === 0) return undefined;
  const who = one
    ? `a member of the Salesforce campaign '${settings.campaigns[0].label}'`
    : `a member of any of ${settings.campaigns.length} Salesforce campaigns`;
  // the same status name can belong to two campaigns: name it once
  const names = Array.from(new Set(statuses.map((status) => status.label)));
  const got =
    names.length <= MAX_NAMES
      ? `the status ${orList(quoted(names))}`
      : `one of ${names.length} statuses`;
  return `When ${who} gets ${got}`;
};

// ---------------------------------------------------------------------------
// Pickers

export type SelectOption = { value: string; label: string };
export type SelectGroup = { label: string; options: SelectOption[] };

export const asOption = (choice: Choice): SelectOption => ({
  value: choice.id,
  label: choice.label,
});

/**
 * The campaigns as picker groups: active ones first, then the others
 * (flow.campaign.group.active / .other). An empty group is left out.
 */
export const campaignGroups = (
  campaigns: Array<{ id: string; label: string; isActive: boolean }>
): SelectGroup[] => {
  const active = campaigns.filter((campaign) => campaign.isActive);
  const other = campaigns.filter((campaign) => !campaign.isActive);
  const groups: SelectGroup[] = [];
  if (active.length > 0) {
    groups.push({ label: "Active campaigns", options: active.map(asOption) });
  }
  if (other.length > 0) {
    groups.push({ label: "Other campaigns", options: other.map(asOption) });
  }
  return groups;
};

/**
 * The member statuses of the chosen campaigns, one group for each campaign
 * (statuses belong to a campaign, and two campaigns can have a status of the
 * same name). The value of an option is "campaignId:statusId".
 */
export const statusGroups = (
  campaigns: Choice[],
  statusesByCampaign: Record<string, Choice[] | undefined>
): SelectGroup[] =>
  campaigns.flatMap((campaign) => {
    const statuses = statusesByCampaign[campaign.id];
    if (!statuses || statuses.length === 0) return [];
    return [
      {
        label: campaign.label,
        options: statuses.map((status) => ({
          value: statusValue(campaign.id, status.id),
          label: status.label,
        })),
      },
    ];
  });

export const statusValue = (campaignId: string, statusId: string) =>
  `${campaignId}:${statusId}`;

/** Chosen stages the stage list no longer has: they were removed in Salesforce. */
export const missingChoices = (
  chosen: Choice[],
  listed: Array<{ id: string }>
): Choice[] => {
  const known = new Set(listed.map((item) => item.id));
  return chosen.filter((choice) => !known.has(choice.id));
};

// ---------------------------------------------------------------------------
// The estimate (flow.opportunity.estimate.*)

const peopleText = (n: number) => (n === 1 ? "1 person" : `${n} people`);

export type EstimateLines = {
  /** "About 40 people would have entered in the last 30 days." */
  headline: string;
  /** Plain facts under the headline, the forward sentence first, only the ones that apply */
  notes: string[];
};

/**
 * What the estimate says. It looks at the Opportunities Encharge has stored, so
 * it costs no Salesforce call.
 *  - A Stage start counts each Opportunity's latest stage change only, so the
 *    real number can be higher (flow.opportunity.estimate.note).
 *  - A Name or Field start has no history to count: Encharge keeps no record of
 *    past changes. It says how many Opportunities match the Filters now, and that
 *    from now on each change starts the flow.
 * When Encharge looked at as many Opportunities as it looks at most, the number
 * is "at least". Filters on a field Encharge does not keep are named as left out.
 */
export const estimateLines = (
  estimate: SalesforceOpportunityEstimate,
  settings: Pick<OpportunityTriggerSettings, "whoEnters">
): EstimateLines => {
  const people = estimate.people;
  const history = estimate.history !== false;
  let headline: string;
  if (history) {
    const window = `in the last ${estimate.days} days`;
    if (people === 0) {
      headline = `No one would have entered ${window}.`;
    } else if (estimate.capped) {
      headline = `At least ${peopleText(people)} would have entered ${window}.`;
    } else {
      headline = `About ${peopleText(people)} would have entered ${window}.`;
    }
  } else if (people === 0) {
    // flow.opportunity.estimate.now.none
    headline = "No one is on the Opportunities that match now.";
  } else if (estimate.capped) {
    headline = `At least ${peopleText(
      people
    )} are on the Opportunities that match now.`;
  } else {
    headline = `About ${peopleText(
      people
    )} are on the Opportunities that match now.`;
  }
  const notes: string[] = history
    ? [
        // Vince, 2026-10-10: the count looks back, this says what happens next
        "From now on, everyone whose Opportunity moves into this stage enters.",
        "This counts each Opportunity's latest stage change only, so the real number can be higher.",
      ]
    : [
        // flow.opportunity.estimate.nohistory
        "Encharge does not keep a record of past changes, so it cannot count how many people this would have started. These are the Opportunities that match the Filters now.",
        "From now on, everyone whose Opportunity changes this way enters.",
      ];
  if (estimate.capped) {
    notes.push(
      "Only part of your Opportunities were counted, so the real number can be higher."
    );
  }
  if (estimate.leftOut && estimate.leftOut.length > 0) {
    notes.push(
      `Encharge does not keep ${estimate.leftOut.join(
        ", "
      )}, so the count does not use ${
        estimate.leftOut.length === 1 ? "that Filter" : "those Filters"
      }.`
    );
  }
  if (estimate.skippedNotInEncharge > 0) {
    notes.push(
      estimate.skippedNotInEncharge === 1
        ? "1 person on those Opportunities is not in Encharge, so they would be skipped."
        : `${estimate.skippedNotInEncharge} people on those Opportunities are not in Encharge, so they would be skipped.`
    );
  }
  if (
    settings.whoEnters === "accountPeople" &&
    estimate.opportunitiesWithoutAccount
  ) {
    notes.push(
      estimate.opportunitiesWithoutAccount === 1
        ? "1 Opportunity has no Account, so nobody would enter for it."
        : `${estimate.opportunitiesWithoutAccount} Opportunities have no Account, so nobody would enter for them.`
    );
  }
  return { headline, notes };
};

// ---------------------------------------------------------------------------
// Where the trigger stands (flow.opportunity.status.*)

export type StatusNotice = {
  tone: "info" | "warning";
  text: string;
};

/**
 * The notice under the trigger's settings for a state of the status route:
 * nothing while it runs. Plain words, the field named by the name the picker
 * shows when the form knows it.
 */
export const statusNotice = (
  status: SalesforceOpportunityStatus | undefined,
  labelOf: (apiName: string) => string = (name) => name
): StatusNotice | undefined => {
  if (!status) return undefined;
  const field = status.field ? labelOf(status.field) : undefined;
  switch (status.state) {
    case "gettingReady":
      return {
        tone: "info",
        text: `Getting ready: reading the current values of ${
          field ?? "this field"
        }. The flow starts for changes made after that.`,
      };
    case "paused":
      return {
        tone: "warning",
        text: `Paused because Salesforce does not let Encharge read ${
          field ?? "a field this flow uses"
        }. Once Encharge can read it again, changes from the last 7 days start the flow.`,
      };
    case "syncOff":
      return {
        tone: "warning",
        text:
          "Opportunity sync is switched off, and this trigger needs it. Switch it on in the Salesforce settings, then switch the flow on.",
      };
    case "notAvailable":
      return {
        tone: "warning",
        text: "This trigger is not available for your account.",
      };
    default:
      return undefined;
  }
};

// ---------------------------------------------------------------------------
// Errors

export type PickerKind =
  | "stages"
  | "campaigns"
  | "statuses"
  | "estimate"
  | "fields"
  | "status";

/** flow.opportunity.error.stages, flow.campaign.error.list, flow.pick.error, flow.opportunity.estimate.error */
const FALLBACK_TEXT: Record<PickerKind, string> = {
  stages: "We could not load the stages from Salesforce. Try again.",
  campaigns:
    "We could not load your campaigns from Salesforce. The Salesforce user who connected Encharge may need the Marketing User permission.",
  statuses: "We could not load this list from Salesforce. Try again.",
  estimate: "We could not count just now. You can still switch the flow on.",
  // flow.opportunity.error.fields
  fields:
    "We could not load the Opportunity fields from Salesforce. Try again.",
  status: "We could not check this trigger just now.",
};

export type TriggerErrorText = {
  text: string;
  /** The Salesforce login needs to be renewed: the screen offers the way to do it */
  reconnect?: boolean;
};

/**
 * What a failed picker or estimate call says. The server's own sentence is said
 * only when it wrote one for the person (it gave an error code of its own);
 * anything else (a server fault, a network error, a scope refusal) has only a
 * technical message, which a customer must not be shown.
 */
export const triggerErrorText = (
  error: Partial<
    Pick<SalesforceTriggerRequestError, "status" | "errorCode" | "message">
  >,
  kind: PickerKind
): TriggerErrorText => {
  switch (error?.errorCode) {
    case "salesforce_v2_not_enabled":
      // sfGeneric.error.notV2
      return {
        text:
          "The new Salesforce sync is not switched on for your account yet.",
      };
    case "salesforce_plan":
      // sfGeneric.error.plan
      return { text: "This feature is not available on your plan." };
    case "salesforce_not_connected":
      // flow.notConnected
      return {
        text:
          "Connect Salesforce first to use this. Go to Settings > Apps > Salesforce.",
      };
    case "salesforce_reconnect":
      return {
        text:
          "Salesforce needs to be reconnected before this can be read. Reconnect Salesforce, then try again.",
        reconnect: true,
      };
    case "salesforce_throttled":
      return {
        text: "The Salesforce API limit was reached. Try again later.",
      };
    case "salesforce_unreachable":
      // sfGeneric.error.salesforceDown
      return { text: "Salesforce did not answer. Try again in a moment." };
    case "salesforce_refused":
    case "salesforce_trigger_settings":
      if (error.message) return { text: error.message };
      break;
    default:
      break;
  }
  if (error?.status === 403) {
    // sfGeneric.error.permission
    return {
      text: "You do not have permission to change Salesforce settings.",
    };
  }
  if (error?.status === 429) {
    return {
      text: "The Salesforce API limit was reached. Try again later.",
    };
  }
  if (error && error.status === undefined) {
    // sfGeneric.error.network
    return {
      text: "We could not reach Encharge. Check your connection and try again.",
    };
  }
  return { text: FALLBACK_TEXT[kind] };
};

/**
 * Whether asking again could help. A refusal the person has to put right first
 * (the account is not on the new sync, the plan has no Salesforce, no permission)
 * will not change by itself; everything else (a fault, the network, the API
 * limit, a login that was renewed in the meantime) might.
 */
export const canTryAgain = (
  error: Pick<SalesforceTriggerRequestError, "status" | "errorCode"> | null
): boolean => {
  if (!error) return false;
  if (
    error.errorCode === "salesforce_v2_not_enabled" ||
    error.errorCode === "salesforce_plan"
  ) {
    return false;
  }
  return !(error.status === 403 && error.errorCode === undefined);
};
